import { Outlet, Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/layout/Header';
import { Button } from '../components/ui';

export default function AdminLayout() {
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Don't show sidebar on login page
  const isLoginPage = location.pathname === '/admin/login';

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: '📊' },
    { path: '/admin/sessions', label: 'Sessions', icon: '📅' },
    { path: '/admin/rooms', label: 'Rooms', icon: '🏢' },
    { path: '/admin/attendees', label: 'Attendees', icon: '👥' },
    { path: '/admin/checkins', label: 'Check-ins', icon: '✓' },
    { path: '/admin/attendance', label: 'Attendance', icon: '📋' },
    { path: '/admin/analytics', label: 'Analytics', icon: '📈' },
    { path: '/admin/announcements', label: 'Announcements', icon: '📢' },
  ];

  const isActive = (path: string) => location.pathname === path;

  if (isLoginPage || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-usc-gray-100">
        <Header isAdmin />
        <main className="container mx-auto px-4 py-6">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-usc-gray-100">
      <Header isAdmin />
      
      <div className="flex">
        {/* Mobile menu button */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="lg:hidden fixed top-28 left-4 z-50 p-2 bg-usc-garnet text-white rounded-md shadow-lg"
          aria-label="Toggle menu"
        >
          {isSidebarOpen ? '✕' : '☰'}
        </button>

        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky top-[6.5rem] left-0 h-[calc(100vh-6.5rem)] w-64 bg-white shadow-lg
            transform transition-transform duration-300 ease-in-out z-40
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
        >
          <div className="flex flex-col h-full">
            {/* User info */}
            <div className="p-4 border-b border-usc-gray-200 bg-usc-gray-50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-usc-garnet text-white rounded-full flex items-center justify-center font-bold">
                  {user?.name?.charAt(0).toUpperCase() || 'A'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-usc-gray-900 truncate">
                    {user?.name || 'Admin User'}
                  </p>
                  <p className="text-xs text-usc-gray-600 truncate">
                    {user?.email || ''}
                  </p>
                </div>
              </div>
              <Button
                onClick={logout}
                variant="outline"
                size="sm"
                className="w-full mt-3"
              >
                Logout
              </Button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-4">
              <ul className="space-y-2">
                {navItems.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setIsSidebarOpen(false)}
                      className={`
                        flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors
                        ${
                          isActive(item.path)
                            ? 'bg-usc-garnet text-white'
                            : 'text-usc-gray-700 hover:bg-usc-gray-100'
                        }
                      `}
                    >
                      <span className="text-xl">{item.icon}</span>
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Footer info */}
            <div className="p-4 border-t border-usc-gray-200 text-xs text-usc-gray-600">
              <p>AI Day Conference</p>
              <p>Admin Dashboard v1.0</p>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {isSidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 p-6 lg:ml-0">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
